import clsx from "clsx";
import { Radio, RadioGroup, Label } from "@headlessui/react";
import { CheckCircleIcon } from "@phosphor-icons/react";

interface RadioOption {
	id: string | number;
	value: string;
	label?: string;
	icon?: React.ReactNode;
}

interface RadioGroupFieldProps {
	id: string;
	label: string;
	value: string;
	options: RadioOption[];
	onChange: (value: string) => void;
	required?: boolean;
	error?: string;
	disabled?: boolean;
	className?: string;
	gridCols?: number;
	showIcons?: boolean;
	disabledOptions?: string[];
	navRef?: boolean;
}

export function RadioGroupField({
	id,
	label,
	value,
	options,
	onChange,
	error,
	required = false,
	disabled = false,
	className = "",
	gridCols = 2,
	showIcons = false,
	disabledOptions = [],
	navRef = false,
}: RadioGroupFieldProps) {
	const colClass = {
		1: "yF-grid-cols-1",
		2: "yF-grid-cols-2",
		3: "yF-grid-cols-2 md:yF-grid-cols-3",
		4: "yF-grid-cols-2 md:yF-grid-cols-4",
		5: "yF-grid-cols-2 md:yF-grid-cols-5",
		6: "yF-grid-cols-2 md:yF-grid-cols-6",
		7: "yF-grid-cols-2 md:yF-grid-cols-7",
		8: "yF-grid-cols-4 md:yF-grid-cols-8",
	}[gridCols];

	return (
		<div className={className}>
			<RadioGroup
				id={id}
				aria-label={label}
				value={value}
				onChange={onChange}
				disabled={disabled}
				aria-required={required}
				aria-describedby={error ? `${id}-error` : ""}
				aria-invalid={!error}
			>
				<Label className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900">
					{label}
				</Label>
				<div
					style={{ gridAutoRows: "1fr" }}
					className={clsx(
						navRef ? "yF-grid-cols-2" : colClass,
						`yF-grid yF-mt-2
             yF-gap-x-2 yF-gap-y-2 md:yF-gap-x-2`,
					)}
				>
					{options.map((item) => {
						const isOptionDisabled = disabledOptions.includes(item.value);
						const optionId = `${id}-option-${item.id}`;
						return (
							<Radio
								key={item.value}
								value={item.value}
								disabled={isOptionDisabled}
								id={optionId}
								aria-describedby={
									isOptionDisabled ? `${optionId}-disabled` : undefined
								}
								className={({ checked }) =>
									clsx(
										"yF-h-full yF-flex-1 yF-min-h-11",
										disabled || isOptionDisabled
											? "yF-opacity-50 yF-cursor-not-allowed"
											: "yF-cursor-pointer",
										isOptionDisabled ? "yF-bg-gray-50" : "yF-bg-white",
										checked && !error
											? "yF-border-[--yF-theme-color] yF-ring-1 yF-ring-[--yF-theme-color]"
											: "",
										error
											? "yF-border-red-600"
											: !checked
												? "yF-border-gray-300"
												: "",
										showIcons ? "yF-flex-1" : "yF-justify-center",
										"yF-relative yF-flex yF-rounded-lg yF-border yF-p-2 yF-shadow-sm focus:yF-outline-none focus-visible:yF-outline-[--yF-theme-color] md:yF-p-3",
									)
								}
							>
								{({ checked }) => (
									<>
										<span
											className={clsx(
												showIcons ? "yF-flex yF-flex-1" : "yF-flex",
											)}
										>
											<span
												className={clsx(
													navRef ? "" : "",
													showIcons ? "yF-items-start" : "",
													"yF-flex yF-flex-col yF-justify-center",
												)}
											>
												{showIcons && item.icon && (
													<div
														className={clsx(
															!checked
																? " yF-text-gray-900"
																: "yF-text-[--yF-theme-color]",
														)}
													>
														{item.icon}
													</div>
												)}
												<Label
													as="span"
													className={clsx(
														!checked ? "yF-flex yF-text-gray-900" : "",
														"yF-text-wrap yF-text-[--yF-theme-color] yF-flex yF-text-sm yF-leading-4",
													)}
												>
													{item.label || item.value}
												</Label>
											</span>
										</span>
										<CheckCircleIcon
											className={clsx(
												!checked ? "yF-invisible" : "",
												"yF-h-5 yF-w-5 yF-text-[--yF-theme-color] yF-absolute yF-bg-white -yF-right-1 -yF-top-1",
											)}
											aria-hidden="true"
											weight="fill"
										/>
										<span
											className={clsx(
												checked
													? "yF-border-[--yF-theme-color]"
													: "yF-border-transparent",

												"yF-pointer-events-none yF-absolute yF-rounded-lg",
											)}
											aria-hidden="true"
										/>
									</>
								)}
							</Radio>
						);
					})}
				</div>
			</RadioGroup>
			{error && (
				<p
					id={`${id}-error`}
					className="yF-mt-1 yF-text-xs yF-text-red-600"
					role="alert"
					aria-live="polite"
				>
					{error}
				</p>
			)}
		</div>
	);
}
